import {AjaxP, DOM, Module} from '@mamdev/cad.static-next-lib';
import Viewport from '../../ts/services/viewport';

interface SportTickerData {
  conference: Conference[];
  solo: string;
  timestamp: string;
}

interface Conference {
  html: string;
}

interface Selectors {
  tickerMenuItems: string;
  liveticker: string;
  tickerReloadBtn: string;
  tickerReloadSpinner: string;
}

/**
 * SportTicker module
 */
export default class SportTicker extends Module {
  private readonly livetickerInterval: number;
  private readonly reloadAnimDuration: number;
  private readonly selectors: Selectors;

  private livetickerNode: HTMLElement | null;
  private reloadBtn: HTMLElement | null;
  private tickerReloadBtn: HTMLElement | null;
  private alreadyReloaded: boolean;
  private livetickerLoading: ReturnType<typeof setTimeout>;
  private livetickerRefresh: ReturnType<typeof setTimeout>;
  private livetickerClickCount: number;

  static attachTo(root: HTMLElement) {
    return new SportTicker(root);
  }

  constructor(root: HTMLElement) {
    super(root);

    this.name_ = 'sportticker';

    this.selectors = {
      tickerMenuItems: '.menu li',
      liveticker: '.js-football-live-ticker',
      tickerReloadBtn: '.reload .reload-btn',
      tickerReloadSpinner: '.reload-btn .icon-spin'
    };

    this.alreadyReloaded = false;
    this.reloadAnimDuration = 3000;
    this.livetickerInterval = 60000;
    this.livetickerClickCount = 0;
  }

  start() {
    this.setupLiveTickerSelectors();
    this.changeMenuTitles();

    // Initialize content refresh interval and setup click handler for refresh button
    if (this.livetickerNode) {
      this.autoRefreshContent();

      this.tickerReloadBtn?.addEventListener('click', e => {
        this.tickerReloadBtnHandler(e);
      });
    }

    this.subscribe('window:resize', () => {
      this.changeMenuTitles();
    });

    // Pubsub between sporttickertool and sportticker to keep them synchronous in conference and detail view.
    this.subscribe('sportticker:toolevent', () => {
      if (this.livetickerNode) {
        this.getLivetickerData(this.livetickerNode, false);
      }
    });

    this.arrowsDisplayer();
  }

  /**
   * Set up global selectors
   */
  setupLiveTickerSelectors() {
    try {
      this.livetickerNode = this.root_.querySelector(this.selectors.liveticker);

      if (this.livetickerNode) {
        this.tickerReloadBtn = this.root_.querySelector(this.selectors.tickerReloadBtn);

        this.reloadBtn = this.tickerReloadBtn?.querySelector(this.selectors.tickerReloadSpinner) ?? null;
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /**
   * Toggles between short and long titles in menu depending on viewport.
   * Note that the active menu item has no link, so we always have to check before re-setting the item's text.
   */
  changeMenuTitles() {
    try {
      const menuItems = this.root_.querySelectorAll<HTMLElement>(this.selectors.tickerMenuItems);

      menuItems.forEach(menuItem => {
        const shortTitle = menuItem.dataset.shortTitle;
        const longTitle = menuItem.dataset.longTitle;

        // If menuItem has a link, change <a> text content, else change <li> text content
        const menuItemLink = menuItem.getElementsByTagName('a')[0];
        menuItem = menuItemLink || menuItem;

        if (Viewport.matches('tablet')) {
          menuItem.textContent = longTitle ?? '';
        } else {
          menuItem.textContent = shortTitle ?? '';
        }
      });
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /**
   * Reload button spinner animation.
   */
  animateReloadBtn() {
    this.reloadBtn?.classList.add('animate');

    this.livetickerLoading = setTimeout(() => {
      this.reloadBtn?.classList.remove('animate');
    }, this.reloadAnimDuration);
  }

  /**
   * arrow icons should always be displayed in the sporticker CADNPCA-3795
   */
  arrowsDisplayer() {
    const gameList = this.root_.getElementsByClassName('js-game-list');

    if (gameList.length > 1 && gameList[0].getElementsByTagName('li').length > 1) {
      const arrowLeft = this.root_.querySelector<HTMLElement>('.icon-arrow-left');
      const arrowRight = this.root_.querySelector<HTMLElement>('.icon-arrow-right');

      if (arrowLeft) arrowLeft.style.display = 'inline';
      if (arrowRight) arrowRight.style.display = 'inline';
    }
  }

  /**
   * Triggers content refresh including ad reload & reload animation.
   * Note that auto-refresh resets livetickerClickCount.
   */
  autoRefreshContent() {
    this.livetickerRefresh = setInterval(() => {
      this.animateReloadBtn();
      this.getLivetickerData(this.livetickerNode);
      this.publish('ad:refreshAll');
      this.livetickerClickCount = 0;
    }, this.livetickerInterval);
  }

  /**
   * Event handler for ticker reload button click event.
   * Also handles AdRefresh logic: refresh ads after every 5th click on refresh button.
   */
  tickerReloadBtnHandler(e: Event) {
    if (this.livetickerClickCount < 4) {
      clearInterval(this.livetickerRefresh);
      this.autoRefreshContent();
      this.livetickerClickCount++;
    } else {
      this.livetickerClickCount = 0;
      this.publish('ad:refreshAll');
    }

    this.alreadyReloaded = true;
    this.animateReloadBtn();
    this.getLivetickerData(this.livetickerNode);
    e.preventDefault();
  }

  /**
   * Renders the liveticker JSON into the content.
   */
  getLivetickerData(parentNode: HTMLElement | null, publishToolEvent = true) {
    try {
      if (!parentNode) return;

      let jsonUrl = parentNode?.dataset.jsonUrl ?? '';

      if (parentNode?.dataset.lastUpdate) {
        jsonUrl += '?timestamp=' + parentNode.dataset.lastUpdate;
      }

      if (!this.alreadyReloaded) {
        this.alreadyReloaded = true;
      } else {
        clearInterval(this.livetickerRefresh);
      }

      // Get JSON and render into liveticker content
      void AjaxP.getJSON(jsonUrl).then(data => {
        this.renderLivetickerContent(parentNode, publishToolEvent, data as SportTickerData);
      });

      // initialize auto refresh
      this.autoRefreshContent();
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /**
   * Renders new content into the liveticker.
   */
  renderLivetickerContent(parentNode: HTMLElement, publishToolEvent: boolean, jsonData: SportTickerData) {
    try {
      if (jsonData) {
        const tickerData = jsonData.conference || jsonData.solo || '';

        // Set data-attribute last-update from json (needed for second call)
        parentNode.dataset.lastUpdate = jsonData.timestamp || Date.now().toString();

        if (tickerData.length > 0) {
          tickerData.forEach(singledata => {
            // Render markup and apply fadeIn (json contains full html markup)
            const renderedTemplate = singledata.html;
            const renderToHtml = DOM.fromHtml(renderedTemplate);
            parentNode.insertBefore(renderToHtml, parentNode.firstChild);

            setTimeout(() => {
              const notshown = parentNode.getElementsByClassName('notshown')[0];
              notshown.classList.remove('notshown');
            }, 30);
          });

          if (publishToolEvent) {
            this.publish('sportticker:conferencevent', {});
          }
        }
      }

      // prevent double loading
      if (this.livetickerLoading) {
        clearTimeout(this.livetickerLoading);
        this.livetickerLoading = setTimeout(() => {
          this.reloadBtn?.classList.remove('animate');
        }, 1000);
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }
}
